import sketchData from '@/data/sketches.json';
import categoryData from '@/data/categories.json';
import explainerData from '@/data/explainers.json';

/**
 * The sketch library.
 *
 * Generated from the production register rather than hand-written, because
 * there are 837 of them. The register is the source of truth: concept,
 * proposition, the copy that appears in the drawing, the collection it was
 * filed under and the authority it was checked against.
 *
 * The register carried two generations of collection name, a broad set using
 * ampersands and a later granular set. Both are kept: `category` is the
 * consolidated one used for navigation, `collection` is the original, so
 * nothing is lost if the taxonomy is revisited.
 */
export interface Sketch {
  id: string;
  slug: string;
  title: string;
  /** The line drawn across the top of the sketch. */
  headline: string;
  standfirst: string;
  takeaway: string;
  proposition: string;
  /** Plain-English description of what the drawing shows. */
  picture: string;
  alt: string;
  category: string;
  categorySlug: string;
  collection: string;
  /** Primary authority the claim was checked against. Empty where none. */
  source: string;
  image: string;
}

export interface Category {
  slug: string;
  name: string;
  intro: string;
  count: number;
}

/**
 * Plain-English explanations, keyed by slug.
 *
 * Written by hand, not derived from the register, because the register holds
 * the copy that goes in the drawing and a one-line proposition, neither of
 * which explains a concept to somebody meeting it for the first time. Being a
 * separate file means they can be written a category at a time without
 * touching the imported data, and a sketch without one simply omits the
 * section.
 */
const EXPLAINERS = explainerData as Record<string, string>;

export const explainerFor = (slug: string): string[] =>
  (EXPLAINERS[slug] ?? '').split('\n\n').map((p) => p.trim()).filter(Boolean);

export const SKETCHES = sketchData as Sketch[];
export const CATEGORIES = categoryData as Category[];

const bySlug = new Map(SKETCHES.map((s) => [s.slug, s]));

export const getSketch = (slug: string) => bySlug.get(slug);
export const getCategory = (slug: string) => CATEGORIES.find((c) => c.slug === slug);
export const sketchesIn = (categorySlug: string) =>
  SKETCHES.filter((s) => s.categorySlug === categorySlug);

/**
 * Related sketches: same category first, then the same original collection,
 * which is narrower and often the better match.
 */
export function related(sketch: Sketch, limit = 6): Sketch[] {
  const sameCollection = SKETCHES.filter(
    (s) => s.slug !== sketch.slug && s.collection === sketch.collection,
  );
  const sameCategory = SKETCHES.filter(
    (s) => s.slug !== sketch.slug && s.categorySlug === sketch.categorySlug && s.collection !== sketch.collection,
  );
  return [...sameCollection, ...sameCategory].slice(0, limit);
}

export function searchSketches(q: string): Sketch[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return SKETCHES;
  return SKETCHES.filter((s) =>
    `${s.title} ${s.headline} ${s.standfirst} ${s.takeaway} ${s.collection}`.toLowerCase().includes(needle),
  );
}

/**
 * A deliberate reading order for someone starting from nothing.
 *
 * Not "the first twelve in the register". These build on each other: what
 * security is protecting, what can go wrong, who might do it, and the handful
 * of ideas that most other controls are an expression of.
 */
export const START_HERE_SLUGS = [
  'cia-triad',
  'threat',
  'vulnerability',
  'risk',
  'attack-surface',
  'trust-boundary',
  'authentication',
  'authorisation',
  'least-privilege',
  'secure-defaults',
  'assume-breach',
  'mfa',
] as const;

export const startHere = (): Sketch[] =>
  START_HERE_SLUGS.map((slug) => bySlug.get(slug)).filter((s): s is Sketch => Boolean(s));
